<template>
  <div>
    <CardTemplate
      v-if="isCardLayout && templateContent && !error"
      :template="templateContent"
      :templateTheme="templateTheme"
      :isSilentAuth="isSilentAuth"
      @buttonClicked="buttonCmd"
    />
    <DisplayTemplate
      v-else-if="templateContent && !error"
      :template="templateContent"
      :templateTheme="templateTheme"
      :isSilentAuth="isSilentAuth"
      @buttonClicked="buttonCmd"
    />
    <ErrorComponent v-if="error" :error="error" />
    <Loading v-else-if="!error && !templateContent" :large-spinner="true">
    </Loading>
    <form></form>
  </div>
</template>

<script setup lang="ts">
import { computed, ref, toRef, watch } from "vue";
import DisplayTemplate from "../components/DisplayTemplate.vue";
import CardTemplate from "../components/CardTemplate.vue";
import Loading from "../components/LoadingComponent.vue";
import ErrorComponent from "../components/ErrorComponent.vue";
import { useRoute } from "vue-router";
import { ButtonNextAction, InputAnswer } from "@/models/template";
import SDK, {
  IChallenge,
  ICustomerAuthnConfig,
  IEngageEndpoint,
  IGetInput,
  INotify,
  IRedirect,
  ISetAuthenticationFactor,
  securityMode,
} from "@entersekt/customerauthn-sdk-web";
import { cacheImages, handleCachedTheme } from "@/store/index";
import { config as env } from "@/config";
import {
  AuthenticationFactorType,
  button,
  theme,
  uiContentJSON,
  uiPage,
} from "@entersekt/types/build/3-7-0/endpoint";

const props = defineProps<{ cachedTheme: theme }>();
const emit = defineEmits(["setBackgroundColor", "setBackgroundImage"]);

const route = useRoute();
const shortname = route.params.channelShortname as string;
const pathCode = route.params.joinCode;
const queryCode = route.query.code?.toString();
const postToParent =
  route.query.post_to_parent?.toString() === "true" ? true : false;
const redirectURI = route.query.redirect_uri?.toString() || null;

const code = pathCode ? pathCode : queryCode;

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const interaction = ref<any>(null);
const error = ref<string>("");

const templateContent = ref<uiContentJSON>();
const templateTheme = ref<theme>();
const isSilentAuth = ref(false);

watch(templateTheme, () => {
  emit("setBackgroundColor", templateTheme.value?.pageBackgroundColor);
  emit("setBackgroundImage", templateTheme.value?.pageBackgroundImageURI);
});

watch(templateContent, () => {
  if (templateContent.value?.summary) {
    document.title = templateContent.value.summary;
  }
});

const isCardLayout = computed(() => {
  const theme = templateTheme.value;
  if (
    theme?.headerBackgroundColor &&
    theme.bodyBackgroundColor &&
    (theme.pageBackgroundImageURI || theme?.pageBackgroundColor)
  ) {
    return true;
  }
  return false;
});

const propsCachedTheme = toRef(props, "cachedTheme");
templateTheme.value = propsCachedTheme.value;

function buttonCmd(
  buttonRole: button.action,
  inputs?: InputAnswer[],
  actionNext?: ButtonNextAction,
) {
  if ((actionNext as string) === "") actionNext = undefined;
  console.log(buttonRole, inputs, actionNext);
  if (buttonRole === "CONTINUE") {
    interaction.value.proceed(inputs, actionNext);
  } else if (buttonRole === "CANCEL" || buttonRole === "CANCEL_AND") {
    if (postToParent) {
      const result = {
        status: "REJECTED",
      };
      window.parent.postMessage(result, redirectURI);
    }
    interaction.value.cancel(actionNext);
  }
}

function getImageURIs(templateContent?: uiContentJSON): string[] {
  const images: string[] = [];
  templateContent?.headerLogoURIs?.forEach((image) => {
    images.push(image);
  });

  if (
    templateContent?.imageURI &&
    !templateContent.imageURI?.includes("javascript:countdownTimer")
  ) {
    images.push(templateContent?.imageURI);
  }

  if (templateContent?.theme?.pageBackgroundImageURI) {
    images.push(templateContent.theme.pageBackgroundImageURI);
  }

  templateContent?.footerLogoURIs?.forEach((image) => {
    images.push(image);
  });

  return images;
}

if (!code) {
  error.value = "No Code Provided";
} else {
  const config: ICustomerAuthnConfig = SDK.configBuilder
    .setConnectionURL(
      process.env.VUE_APP_SOCKET_CONNECTION_URL || env.browserChannelURL || "",
    )
    .setTokenEndpointURL(
      process.env.VUE_APP_TOKEN_CONNECTION_URL ||
        env.endpointAuthenticatorURL ||
        "",
    )
    .setSignalCollectorURL(
      process.env.VUE_APP_SIGNAL_COLLECTOR_URL || env.signalCollectorURL || "",
    )
    .setChannelShortName(shortname)
    .setTimeoutInMilliseconds(10000)
    .setRedirectDelayInMilliseconds(2000)
    .setSecurityProviderConfig({ mode: env.securityMode as securityMode })
    .setShouldSubmitRiskSignalsOnInit(
      (env.securityMode as securityMode) == securityMode.Enforced,
    )
    .build();

  SDK.init(config)
    .then((sdk) => {
      sdk
        .onSetAuthenticationFactor()
        .subscribe(async (result: ISetAuthenticationFactor) => {
          interaction.value = result;
          if (result.uiContent && "contentJSON" in result.uiContent) {
            templateContent.value = (result.uiContent as uiPage).contentJSON;
          } else {
            templateContent.value = result.uiContent as uiContentJSON;
          }

          const images: string[] = getImageURIs(templateContent.value);
          cacheImages(images);

          templateTheme.value = handleCachedTheme(
            { ...templateTheme.value },
            { ...templateContent.value?.theme },
          );
        });

      sdk.onChallengeCustomer().subscribe(async (result: IChallenge) => {
        interaction.value = result;
        if (result.uiContent && "contentJSON" in result.uiContent) {
          templateContent.value = (result.uiContent as uiPage).contentJSON;
        } else {
          templateContent.value = result.uiContent as uiContentJSON;
        }

        const images: string[] = getImageURIs(templateContent.value);
        cacheImages(images);

        templateTheme.value = handleCachedTheme(
          { ...templateTheme.value },
          { ...templateContent.value?.theme },
        );

        if (
          result.authenticationFactorType ===
          AuthenticationFactorType.BROWSER_CREDENTIAL_SILENT
        ) {
          isSilentAuth.value = true;
          buttonCmd(button.action.CONTINUE);
        }
      });

      sdk.onNotify().subscribe((result: INotify) => {
        interaction.value = result;
        if (result.uiContent && "contentJSON" in result.uiContent) {
          templateContent.value = (result.uiContent as uiPage).contentJSON;
        } else {
          templateContent.value = result.uiContent as uiContentJSON;
        }

        const images: string[] = getImageURIs(templateContent.value);
        cacheImages(images);

        templateTheme.value = handleCachedTheme(
          { ...templateTheme.value },
          { ...templateContent.value?.theme },
        );
        result.proceed();
      });

      sdk.onRedirect().subscribe((result: IRedirect) => {
        interaction.value = result;
        if (result.uiContent && "contentJSON" in result.uiContent) {
          templateContent.value = (result.uiContent as uiPage).contentJSON;
        } else {
          templateContent.value = result.uiContent as uiContentJSON;
        }

        const images: string[] = getImageURIs(templateContent.value);
        cacheImages(images);

        templateTheme.value = handleCachedTheme(
          { ...templateTheme.value },
          { ...templateContent.value?.theme },
        );
      });

      sdk.onInteractionCompleted().subscribe();

      sdk.onIdentifyCustomer().subscribe(async (result: IEngageEndpoint) => {
        interaction.value = result;
        if (result.uiContent && "contentJSON" in result.uiContent) {
          templateContent.value = (result.uiContent as uiPage).contentJSON;
        } else {
          templateContent.value = result.uiContent as uiContentJSON;
        }

        const images: string[] = getImageURIs(templateContent.value);
        cacheImages(images);

        templateTheme.value = handleCachedTheme(
          { ...templateTheme.value },
          { ...templateContent.value?.theme },
        );
      });

      sdk.onGetInput().subscribe(async (result: IGetInput) => {
        interaction.value = result;
        if (result.uiContent && "contentJSON" in result.uiContent) {
          templateContent.value = (result.uiContent as uiPage).contentJSON;
        } else {
          templateContent.value = result.uiContent as uiContentJSON;
        }

        const images: string[] = getImageURIs(templateContent.value);
        cacheImages(images);

        templateTheme.value = handleCachedTheme(
          { ...templateTheme.value },
          { ...templateContent.value?.theme },
        );
      });

      sdk
        .joinInteraction({
          entersektJoinCode: code as string,
          postToParent,
          originRedirectURI: redirectURI,
        })
        .then(() => {
          console.log("joined");
        })
        .catch((err) => {
          console.error(err.toString());
          error.value = `Challenge not found. It has either expired or the link is invalid.`;
        });
    })
    .catch((err) => {
      console.error(err);
      error.value = `${err.toString()}`;
    });
}
</script>
